import React, {useCallback, useMemo} from 'react';

import {Artifact, ArtifactComponentProps, ArtifactType} from 'llm/chat-library/ArtifactTypes';
import {DataCaptureDraftDocument} from 'llm/data-capture/DataCaptureDraftDocument';
import {DataCaptureAgentState, DraftDocument} from 'llm/data-capture/DataCaptureTypes';
import DocumentActions, {LATEST_DOCUMENT_VERSION} from 'notebook/documents/DocumentActions';
import {waitForEntryOrTemplateSaved} from 'notebook/entries/BaseEntryUtil';

export interface UseDocumentArtifactOptions {
  /** The application state from the agent - will extract draft_document if present */
  latestApplicationState: object | null;
}

/** Type alias for document artifact */
export type DocumentArtifact = Artifact<DraftDocument>;

/**
 * Component that renders a document artifact.
 * Receives the draft document as the `data` prop per the Artifact contract.
 */
export function DocumentArtifactComponent({
  data: draftDocument,
  editable,
  onClose,
  renderHeader,
}: ArtifactComponentProps<DraftDocument>): React.ReactElement {
  return (
    <DataCaptureDraftDocument
      draftDocument={draftDocument}
      isDocumentEditable={editable}
      locked={!editable}
      onClose={onClose}
      renderHeader={renderHeader}
    />
  );
}

function hasDataCaptureDraft(
  state: object
): state is Required<Pick<DataCaptureAgentState, 'draft_document'>> {
  // We don't have proper typing for agent application state from the backend, so this is a bit gross.
  return 'draft_document' in state;
}

/**
 * Hook for managing a document artifact in the chat.
 *
 * This handles:
 * - Extracting the draft document from application state
 * - Providing an open method to open the document in the editor
 * - Waiting for document saves before sending messages
 * - Providing a component to render the document
 *
 * @returns The artifact, or null if no document exists
 */
export function useDocumentArtifact({
  latestApplicationState,
}: UseDocumentArtifactOptions): DocumentArtifact | null {
  // Extract draft document from the application state
  const draftDocument = useMemo(
    () =>
      latestApplicationState && hasDataCaptureDraft(latestApplicationState)
        ? latestApplicationState?.draft_document
        : null,
    [latestApplicationState]
  );

  // Open the document in the editor
  const open = useCallback(() => {
    if (draftDocument?.document_api_identifier) {
      DocumentActions.open(draftDocument.document_api_identifier, {
        documentVersionId: LATEST_DOCUMENT_VERSION,
      });
    }
  }, [draftDocument?.document_api_identifier]);

  const beforeSendMessage = useCallback(async () => {
    if (draftDocument) {
      await waitForEntryOrTemplateSaved(draftDocument.document_api_identifier, 500, 10);
    }
  }, [draftDocument]);

  const artifact = useMemo((): DocumentArtifact | null => {
    if (!draftDocument) {
      return null;
    }
    return {
      artifactType: ArtifactType.DOCUMENT,
      open,
      beforeSendMessage,
      data: draftDocument,
      Component: DocumentArtifactComponent,
    };
  }, [draftDocument, open, beforeSendMessage]);

  return artifact;
}
